/**
 * OAProductsApi - Batch product fetching with caching
 * Fetches products by handles or IDs using Storefront GraphQL API
 * Transforms to REST-compatible format for Vue components
 * Depends on OAStorefrontApi being loaded first
 */
window.OAProductsApi = {
  cache: new Map(),

  // Query for fetching products by handles
  handleQuery: `query getProductsByHandles($query: String!) {
    products(first: 100, query: $query) {
      nodes {
        id
        handle
        title
        tags
        productType
        availableForSale
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount }
        }
        images(first: 50) {
          nodes { url }
        }
        media(first: 10) {
          nodes {
            mediaContentType
            ... on Video {
              sources { url mimeType }
            }
          }
        }
        variants(first: 100) {
          nodes {
            id
            sku
            title
            price { amount }
            compareAtPrice { amount }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
    }
  }`,

  // Query for fetching products by IDs
  idQuery: `query getProductsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        tags
        productType
        availableForSale
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount }
        }
        images(first: 50) {
          nodes { url }
        }
        media(first: 10) {
          nodes {
            mediaContentType
            ... on Video {
              sources { url mimeType }
            }
          }
        }
        variants(first: 100) {
          nodes {
            id
            sku
            title
            price { amount }
            compareAtPrice { amount }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
    }
  }`,

  /**
   * Transform GraphQL product to REST-compatible format
   * Matches structure expected by Vue components
   */
  transformProduct: function(gql) {
    if (!gql) return null;
    var self = this;

    var priceMin = Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100);
    var compareAtPrice = gql.compareAtPriceRange.minVariantPrice
      ? Math.round(parseFloat(gql.compareAtPriceRange.minVariantPrice.amount) * 100)
      : null;

    return {
      id: parseInt(OAStorefrontApi.toNumericId(gql.id)),
      handle: gql.handle,
      title: gql.title,
      tags: gql.tags,
      product_type: gql.productType,
      available: gql.availableForSale,
      price: priceMin,
      price_min: priceMin,
      compare_at_price: compareAtPrice,
      price_ratio: compareAtPrice && compareAtPrice > priceMin
        ? Math.round((1 - priceMin / compareAtPrice) * 100)
        : 0,
      variants: gql.variants.nodes.map(function(v) {
        return {
          id: parseInt(OAStorefrontApi.toNumericId(v.id)),
          sku: v.sku,
          title: v.title,
          option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
          option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
          option3: v.selectedOptions[2] ? v.selectedOptions[2].value : null,
          price: Math.round(parseFloat(v.price.amount) * 100),
          compare_at_price: v.compareAtPrice
            ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
            : null,
          inventory_quantity: v.availableForSale ? 1 : 0,
          available: v.availableForSale
        };
      }),
      product_image: gql.images.nodes[0] ? gql.images.nodes[0].url : null,
      image: gql.images.nodes[0] ? { src: gql.images.nodes[0].url } : null,
      images: gql.images.nodes.map(function(img) { return { src: img.url }; }),
      media: gql.media.nodes.map(function(m) {
        return {
          media_type: m.mediaContentType.toLowerCase(),
          sources: m.sources || []
        };
      })
    };
  },

  /**
   * Build handle query string for Storefront API
   * @param {Array<string>} handles - Product handles
   * @returns {string} Query string like "handle:h1 OR handle:h2"
   */
  buildHandleQuery: function(handles) {
    return handles.map(function(h) { return 'handle:"' + h + '"'; }).join(' OR ');
  },

  /**
   * Normalize handle-like input to a Shopify product handle
   * Supports raw handles, /products/{handle}, and full product URLs
   * @param {string} value
   * @returns {string|null}
   */
  normalizeHandle: function(value) {
    if (typeof value !== 'string') return null;
    var handle = value.trim();
    if (!handle) return null;
    handle = handle.replace(/^https?:\/\/[^/]+/i, '');
    handle = handle.replace(/^\/?products\//i, '');
    handle = handle.split('?')[0].split('#')[0];
    handle = handle.replace(/^\/+|\/+$/g, '');
    return handle ? handle.toLowerCase() : null;
  },

  /**
   * REST fallback for product handle
   * @param {string} handle
   * @returns {Promise<Object|null>}
   */
  fetchByHandleRest: function(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function(response) {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function() { return null; });
  },

  /**
   * Fetch products by handles
   * @param {Array<string>} handles - Product handles to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input handles
   */
  fetchByHandles: function(handles) {
    var self = this;
    if (!handles || handles.length === 0) {
      return Promise.resolve([]);
    }

    var requestedHandles = handles.map(function(h) { return self.normalizeHandle(h); });
    var validHandles = requestedHandles.filter(function(h) { return Boolean(h); });

    if (validHandles.length === 0) {
      return Promise.resolve(handles.map(function() { return null; }));
    }

    // Check cache first
    var uncached = validHandles.filter(function(h) { return !self.cache.has(h); });

    if (uncached.length === 0) {
      return Promise.resolve(requestedHandles.map(function(h) { return h ? (self.cache.get(h) || null) : null; }));
    }

    // Fetch uncached products
    var queryString = this.buildHandleQuery(uncached);
    return OAStorefrontApi.fetch(this.handleQuery, { query: queryString })
      .then(function(data) {
        if (!data || !data.products || !data.products.nodes) {
          return Promise.all(uncached.map(function(h) { return self.fetchByHandleRest(h); }))
            .then(function(products) {
              products.forEach(function(product, index) {
                var handle = uncached[index];
                if (product) self.cache.set(handle, product);
              });
              return requestedHandles.map(function(h) { return h ? (self.cache.get(h) || null) : null; });
            });
        }

        // Cache fetched products by handle
        data.products.nodes.forEach(function(node) {
          if (node) {
            var product = self.transformProduct(node);
            var normalizedNodeHandle = self.normalizeHandle(node.handle);
            if (normalizedNodeHandle) {
              self.cache.set(normalizedNodeHandle, product);
            }
          }
        });

        var missing = uncached.filter(function(h) { return !self.cache.has(h); });
        if (missing.length === 0) {
          return requestedHandles.map(function(h) { return h ? (self.cache.get(h) || null) : null; });
        }

        return Promise.all(missing.map(function(h) { return self.fetchByHandleRest(h); }))
          .then(function(products) {
            products.forEach(function(product, index) {
              var handle = missing[index];
              if (product) self.cache.set(handle, product);
            });
            return requestedHandles.map(function(h) { return h ? (self.cache.get(h) || null) : null; });
          });
      });
  },

  /**
   * Fetch products by IDs
   * @param {Array<number|string>} ids - Product IDs to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input IDs
   */
  fetchByIds: function(ids) {
    var self = this;
    if (!ids || ids.length === 0) {
      return Promise.resolve([]);
    }

    // Check cache first (by ID)
    var uncached = ids.filter(function(id) { return !self.cache.has('id:' + id); });

    if (uncached.length === 0) {
      return Promise.resolve(ids.map(function(id) { return self.cache.get('id:' + id); }));
    }

    // Fetch uncached products
    var gids = OAStorefrontApi.toGids('Product', uncached);
    return OAStorefrontApi.fetch(this.idQuery, { ids: gids })
      .then(function(data) {
        if (!data || !data.nodes) {
          return ids.map(function(id) { return self.cache.get('id:' + id) || null; });
        }

        // Cache fetched products by ID and handle
        data.nodes.forEach(function(node) {
          if (node) {
            var product = self.transformProduct(node);
            var numericId = OAStorefrontApi.toNumericId(node.id);
            self.cache.set('id:' + numericId, product);
            var normalizedNodeHandle = self.normalizeHandle(node.handle);
            if (normalizedNodeHandle) {
              self.cache.set(normalizedNodeHandle, product);
            }
          }
        });

        // Return in original order
        return ids.map(function(id) { return self.cache.get('id:' + id) || null; });
      });
  }
};
